import React, { useState, useRef, useEffect, useCallback } from "react";

// Photo viewer with native-feeling pinch-to-zoom (mobile) and wheel-zoom
// (desktop). One-finger / mouse drag pans the image once zoomed.
// No tap-to-zoom, no zoom buttons.
export default function ZoomableImage({ src, alt }) {
  const MIN = 1;
  const MAX = 4;
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const boxRef = useRef(null);
  const gesture = useRef({
    mode: null, // 'pinch' | 'pan' | null
    startDist: 0,
    startScale: 1,
    startPan: { x: 0, y: 0 },
    startPointer: { x: 0, y: 0 },
  });

  // Reset when source changes
  useEffect(() => {
    setScale(1);
    setPan({ x: 0, y: 0 });
  }, [src]);

  const clampPan = useCallback((nx, ny, s) => {
    const el = boxRef.current;
    if (!el) return { x: nx, y: ny };
    const rect = el.getBoundingClientRect();
    const maxX = (rect.width * (s - 1)) / 2;
    const maxY = (rect.height * (s - 1)) / 2;
    return {
      x: Math.max(-maxX, Math.min(maxX, nx)),
      y: Math.max(-maxY, Math.min(maxY, ny)),
    };
  }, []);

  const distance = (t1, t2) =>
    Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      gesture.current = {
        mode: "pinch",
        startDist: distance(e.touches[0], e.touches[1]),
        startScale: scale,
        startPan: pan,
        startPointer: { x: 0, y: 0 },
      };
    } else if (e.touches.length === 1 && scale > 1) {
      gesture.current = {
        mode: "pan",
        startDist: 0,
        startScale: scale,
        startPan: pan,
        startPointer: { x: e.touches[0].clientX, y: e.touches[0].clientY },
      };
    } else {
      gesture.current.mode = null;
    }
  };

  const onTouchMove = (e) => {
    const g = gesture.current;
    if (g.mode === "pinch" && e.touches.length === 2) {
      e.preventDefault();
      const d = distance(e.touches[0], e.touches[1]);
      const ratio = d / g.startDist;
      const next = Math.max(MIN, Math.min(MAX, g.startScale * ratio));
      setScale(next);
      if (next <= 1) setPan({ x: 0, y: 0 });
      else setPan((p) => clampPan(p.x, p.y, next));
    } else if (g.mode === "pan" && e.touches.length === 1) {
      e.preventDefault();
      const dx = e.touches[0].clientX - g.startPointer.x;
      const dy = e.touches[0].clientY - g.startPointer.y;
      setPan(clampPan(g.startPan.x + dx, g.startPan.y + dy, scale));
    }
  };

  const onTouchEnd = (e) => {
    if (e.touches.length === 0) {
      gesture.current.mode = null;
    } else if (e.touches.length === 1 && gesture.current.mode === "pinch") {
      // fall back to pan
      gesture.current = {
        mode: "pan",
        startDist: 0,
        startScale: scale,
        startPan: pan,
        startPointer: { x: e.touches[0].clientX, y: e.touches[0].clientY },
      };
    }
  };

  // Desktop: mouse wheel to zoom, drag to pan
  const onWheel = (e) => {
    e.preventDefault();
    const delta = -e.deltaY;
    const next = Math.max(MIN, Math.min(MAX, scale + delta * 0.0025));
    if (next <= 1) setPan({ x: 0, y: 0 });
    else setPan((p) => clampPan(p.x, p.y, next));
    setScale(next);
  };

  const onPointerDown = (e) => {
    if (e.pointerType === "touch") return; // touch handled separately
    if (scale <= 1) return;
    gesture.current = {
      mode: "pan",
      startDist: 0,
      startScale: scale,
      startPan: pan,
      startPointer: { x: e.clientX, y: e.clientY },
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (e.pointerType === "touch") return;
    const g = gesture.current;
    if (g.mode !== "pan") return;
    const dx = e.clientX - g.startPointer.x;
    const dy = e.clientY - g.startPointer.y;
    setPan(clampPan(g.startPan.x + dx, g.startPan.y + dy, scale));
  };
  const onPointerUp = (e) => {
    if (e.pointerType === "touch") return;
    if (gesture.current.mode === "pan") gesture.current.mode = null;
  };

  return (
    <div
      ref={boxRef}
      onWheel={onWheel}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      className="relative h-full w-full flex items-center justify-center overflow-hidden select-none"
      style={{
        touchAction: "none",
        cursor: scale > 1 ? "grab" : "default",
      }}
      data-testid="zoomable-image"
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        className="h-full w-full object-contain pointer-events-none"
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
          transformOrigin: "center center",
          transition: gesture.current.mode ? "none" : "transform 0.15s ease-out",
        }}
      />
    </div>
  );
}
